import { useMemo, useState } from 'react';
import { Alert, Badge, Button, ErrorBanner, SearchCombobox } from '@zarya/ui';
import { useResourcesForPolicy } from './Policies.api';
import { selectionText, type PolicyGrant, type Resource, type Tool } from './PolicyModel';
import { t } from '@app/i18n';
import { withEmphasis } from '@app/i18n/emphasis';
import s from './Policies.module.css';
import { RISK_TOM } from '@app/risk';

export interface PolicyCapabilitiesTabProps {
  concessoes: PolicyGrant[];
  onConcessoesChange: (concessoes: PolicyGrant[]) => void;
  editable: boolean;
}

/**
 * Aba **Capabilities** do drawer (T12, FR-080).
 *
 * Limite só aparece em parâmetro que a tool **declara** aceitar
 * (`constrainable`): limite em parâmetro não aceito é impossível pela interface
 * (9.11) — e é exatamente o que o middleware recusaria com `limite_nao_aceito`.
 * Tool de resource fora do gateway ganha o aviso "sem enforcement no PDP"
 * (9.12).
 *
 * SELEÇÃO EM MASSA AGE SÓ NO QUE O FILTRO MOSTRA (03/09)
 *
 *   O catálogo tem dezenas de tools e a grade cabe numa tela. «Selecionar
 *   filtradas» e «Limpar filtradas» agem sobre as linhas que o filtro deixou
 *   visíveis, e a seleção fora do filtro é **preservada**; o ☑ do cabeçalho
 *   marca a página filtrada, nunca o catálogo inteiro. Um «marcar tudo» que
 *   alcança o que não se vê concede acesso que ninguém leu.
 *
 * SEM «A2A CONCEDIDO» (DEF-32, 04/09)
 *
 *   A seção que fechava esta aba deixava marcar «identidades que a regra deixa
 *   invocar — além do que o contrato já permite». Era resto do modelo antigo
 *   (política concede à identidade; união): o que ela gravava ia ao PDP como
 *   `policies[slug].agents`, e o Rego nunca leu isso — A2A é decidido só por
 *   `workloads[chamador].agents`, o contrato. No modelo vivo (interseção: a
 *   regra humana afirma, o contrato concede) quem diz que um agente invoca
 *   outro é Identity Access › Outgoing, e só. A tela não envia mais `a2a`.
 */
export function PolicyCapabilitiesTab({
  concessoes,
  onConcessoesChange,
  editable,
}: PolicyCapabilitiesTabProps) {
  const [search, setBusca] = useState('');
  const [resourceId, setResourceId] = useState<string | null>(null);
  const [soConcedidas, setSoConcedidas] = useState(false);

  const resources = useResourcesForPolicy(true);
  const lista = useMemo(() => resources.data?.items ?? [], [resources.data]);

  const linhas = useMemo(() => {
    const q = search.trim().toLowerCase();
    return lista
      .filter((resource: Resource) => (resourceId ? resource.id === resourceId : true))
      .flatMap((resource) => resource.tools.map((tool) => ({ resource, tool })))
      .filter(({ resource, tool }) =>
        q
          ? tool.gateway_name.toLowerCase().includes(q) ||
            (tool.description ?? '').toLowerCase().includes(q) ||
            resource.name.toLowerCase().includes(q)
          : true,
      )
      .filter(({ tool }) =>
        soConcedidas ? concessoes.some((grant) => grant.tool_id === tool.id) : true,
      );
  }, [lista, search, resourceId, soConcedidas, concessoes]);

  /** Quantas tools o catálogo inteiro tem — o «de N» do contador. */
  const catalogSize = useMemo(
    () => lista.reduce((soma: number, resource: Resource) => soma + resource.tools.length, 0),
    [lista],
  );
  /** Há filtro aplicado? Só então o contador diz «· mostrando m». */
  const filtering = Boolean(search.trim()) || resourceId !== null || soConcedidas;

  const grantDe = (tool: Tool) => concessoes.find((grant) => grant.tool_id === tool.id);

  const allFilteredGranted =
    linhas.length > 0 && linhas.every(({ tool }) => Boolean(grantDe(tool)));

  /** Concede **as filtradas**, somando ao que já estava concedido fora do filtro. */
  function selectFiltered() {
    const news = linhas
      .filter(({ tool }) => !grantDe(tool))
      .map(({ resource, tool }) => ({
        resource_id: resource.id,
        tool_id: tool.id,
        constraints: {},
      }));
    if (news.length) onConcessoesChange([...concessoes, ...news]);
  }

  /**
   * Tira **as filtradas**, e só elas. O limite das que ficam não se toca: o
   * usuário está retirando o que vê, não reescrevendo o resto da regra.
   */
  function clearFiltered() {
    const noFilter = new Set(linhas.map(({ tool }) => tool.id));
    // `tool_id` é opcional desde o DEF-29 (uma concessão `kind: agent` não tem tool). Sem alvo,
    // `has(undefined)` é falso e a linha fica — que é o certo: esta aba retira tools filtradas, e
    // uma delegação não está entre as filtradas.
    onConcessoesChange(
      concessoes.filter((grant) => grant.tool_id === undefined || !noFilter.has(grant.tool_id)),
    );
  }

  function alternar(resource: Resource, tool: Tool) {
    const current = grantDe(tool);
    if (current) {
      onConcessoesChange(concessoes.filter((grant) => grant.tool_id !== tool.id));
      return;
    }
    onConcessoesChange([
      ...concessoes,
      { resource_id: resource.id, tool_id: tool.id, constraints: {} },
    ]);
  }

  function defineLimit(tool: Tool, parametro: string, valor: string) {
    const num = valor.trim() === '' ? null : Number(valor);
    onConcessoesChange(
      concessoes.map((grant) => {
        if (grant.tool_id !== tool.id) return grant;
        const constraints = { ...(grant.constraints ?? {}) };
        // Limite apagado sai do documento: `{}` é "sem limite", não "limite zero".
        if (num === null || Number.isNaN(num)) delete constraints[parametro];
        else constraints[parametro] = num;
        return { ...grant, constraints };
      }),
    );
  }

  return (
    <div className={s.aba}>
      <div className={s.abaBarra} data-testid="capabilities-bar">
        <input
          type="search"
          className={s.busca}
          value={search}
          placeholder={t('humanAccess.capabilities.search')}
          aria-label={t('humanAccess.capabilities.search')}
          onChange={(e) => setBusca(e.target.value)}
        />
        <SearchCombobox
          label={t('humanAccess.capabilities.resource')}
          size="sm"
          placeholder={t('humanAccess.capabilities.resource.placeholder')}
          options={lista.map((resource) => ({
            value: resource.id,
            label: resource.name,
            hint: resource.gateway_id,
          }))}
          value={resourceId}
          onChange={setResourceId}
        />
        <label className={s.pilula}>
          <input
            type="checkbox"
            checked={soConcedidas}
            onChange={(e) => setSoConcedidas(e.target.checked)}
          />
          {t('humanAccess.capabilities.grantedOnly')}
        </label>
        {editable ? (
          <>
            <Button variant="out" size="sm" onClick={selectFiltered}>
              {t('humanAccess.capabilities.selectFiltered', { n: linhas.length })}
            </Button>
            <Button variant="soft" size="sm" onClick={clearFiltered}>
              {t('humanAccess.capabilities.clearFiltered')}
            </Button>
          </>
        ) : null}
        <span className={s.nota}>
          {selectionText(concessoes.length, catalogSize, filtering ? linhas.length : null)}
        </span>
      </div>

      {resources.isError ? (
        <ErrorBanner
          error={resources.error}
          title={t('humanAccess.capabilities.readError')}
          onRetry={() => void resources.refetch()}
          hideLink
        />
      ) : null}

      {resources.isLoading ? (
        <p className={s.vazio}>{t('humanAccess.capabilities.loading')}</p>
      ) : null}

      {!resources.isLoading && !resources.isError ? (
        <table className={s.grade}>
          <caption className="sr-only">{t('humanAccess.capabilities.caption')}</caption>
          <thead>
            <tr>
              <th scope="col" className={s.colCheck}>
                <input
                  type="checkbox"
                  checked={allFilteredGranted}
                  disabled={!editable || linhas.length === 0}
                  aria-label={t('humanAccess.capabilities.markFiltered')}
                  onChange={() => (allFilteredGranted ? clearFiltered() : selectFiltered())}
                />
              </th>
              <th scope="col">{t('humanAccess.capabilities.col.tool')}</th>
              <th scope="col">{t('humanAccess.capabilities.col.resource')}</th>
              <th scope="col">{t('humanAccess.capabilities.col.risk')}</th>
              <th scope="col">{t('humanAccess.capabilities.col.limit')}</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map(({ resource, tool }) => {
              const grant = grantDe(tool);
              return (
                <tr key={tool.id} data-testid={`tool-${tool.gateway_name}`}>
                  <td className={s.colCheck}>
                    <input
                      type="checkbox"
                      checked={Boolean(grant)}
                      disabled={!editable}
                      aria-label={tool.gateway_name}
                      onChange={() => alternar(resource, tool)}
                    />
                  </td>
                  <td>
                    <strong className={s.mono}>{tool.gateway_name}</strong>
                    {tool.description ? (
                      <span className={s.descricao}>{tool.description}</span>
                    ) : null}
                  </td>
                  <td>
                    <span className={s.recurso}>
                      {resource.name}
                      {resource.out_of_gateway ? (
                        <Badge tone="warning" tip={t('humanAccess.capabilities.outOfGateway.tip')}>
                          {t('humanAccess.capabilities.outOfGateway')}
                        </Badge>
                      ) : null}
                    </span>
                  </td>
                  <td>
                    <Badge tone={RISK_TOM[tool.risk_displayed]}>{tool.risk_displayed}</Badge>
                  </td>
                  <td>
                    {tool.constrainable.length === 0 ? (
                      <span className={s.vazio}>{t('humanAccess.capabilities.noLimit')}</span>
                    ) : (
                      <span className={s.limites}>
                        {tool.constrainable.map((parametro) => (
                          <label key={parametro} className={s.limite}>
                            <span className={s.limiteNome}>{parametro}</span>
                            <input
                              type="number"
                              min={0}
                              className={s.limiteCampo}
                              aria-label={t('humanAccess.capabilities.limit.aria', {
                                param: parametro,
                                tool: tool.gateway_name,
                              })}
                              disabled={!editable || !grant}
                              value={grant?.constraints?.[parametro] ?? ''}
                              onChange={(e) => defineLimit(tool, parametro, e.target.value)}
                            />
                          </label>
                        ))}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}

      {!resources.isLoading && !resources.isError && linhas.length === 0 ? (
        <p className={s.vazio}>{t('humanAccess.capabilities.empty')}</p>
      ) : null}
    </div>
  );
}

/** Aviso da aba em modo Rego manual (9.13) — a grade não se aplica. */
export function CapabilitiesInModeRego() {
  return (
    <Alert tone="warning" title={t('humanAccess.capabilities.regoMode.title')}>
      {withEmphasis(t('humanAccess.capabilities.regoMode.body'))}
    </Alert>
  );
}
